/**
 * Knowledge base for Velnoris AI.
 *
 * Two tiers:
 *  - Platform topics: answered from published Velnoris information.
 *  - Open topics: general career, leadership and everyday questions the
 *    assistant will engage with directly rather than deflecting.
 *
 * The assistant still never claims access to personal records, application
 * decisions or confidential mandate detail.
 */

export interface AssistantLink {
  label: string;
  to: string;
}

export interface AssistantTopic {
  id: string;
  /** Lower-case terms matched against the question. */
  keywords: string[];
  answer: string;
  links?: AssistantLink[];
}

export const ASSISTANT_DISCLOSURE =
  'Ask me anything — about Velnoris, your career, or whatever else is on your mind. I answer platform questions from our published information. I cannot see your profile, applications or confidential mandate detail, and I never make decisions about candidacy.';

export const ASSISTANT_GREETING =
  'Hello. I am Velnoris AI. I can help with anything on this site — registering, how mandates are verified, what happens to your information — and just as happily with the wider things: interview preparation, negotiating an offer, a first ninety days, or a decision you are turning over. What would you like to talk about?';

export const suggestedQuestions = [
  'How do I register?',
  'Help me prepare for an interview',
  'How should I negotiate an offer?',
  'What does “Confidential Mandate” mean?',
  'Who sees my profile?',
  'I am not sure what I want next',
];

/* ------------------------------- Platform ---------------------------------- */

const platformTopics: AssistantTopic[] = [
  {
    id: 'register',
    keywords: ['register', 'registration', 'sign up', 'signup', 'join the network', 'create account', 'new account'],
    answer:
      'Registration takes a few minutes and needs only your name, email and a portal password. Everything else — title, experience, LinkedIn and what you are looking for — is optional and can be completed later in your portal. Registering places you in our research network; it is not an application, and it does not guarantee consideration for any particular mandate.',
    links: [
      { label: 'Register your interest', to: '/register' },
      { label: 'Sign in to your portal', to: '/sign-in' },
    ],
  },
  {
    id: 'privacy',
    keywords: ['who sees', 'my profile', 'privacy', 'my data', 'shared with', 'gdpr', 'delete my', 'confidentiality'],
    answer:
      'Nothing about you reaches an employer without your written authorization for that specific mandate. You authorize each presentation separately, and declining has no effect on your candidacy or your access to any opportunity. You can request a copy of your data, a correction, or deletion at any time.',
    links: [
      { label: 'Privacy policy', to: '/legal/privacy-policy' },
      { label: 'Candidate consent statement', to: '/legal/candidate-consent' },
    ],
  },
  {
    id: 'statuses',
    keywords: ['status', 'statuses', 'under review', 'where is my application', 'heard back', 'progress of my'],
    answer:
      'Every application shows a dated history rather than a vague progress bar. “Under preliminary review” means a consultant is checking it against the published requirements. “Candidate consent required” means we need your authorization before anything is shared. “On hold” usually means the client paused the mandate — it is not a decision about you. A decision not to progress is always recorded with a named reviewer and a written reason.',
    links: [{ label: 'Your applications', to: '/portal/candidate/applications' }],
  },
  {
    id: 'verification',
    keywords: ['verified', 'verification', 'confidential mandate', 'proposed opportunity', 'market matched', 'market-matched', 'is this a real job'],
    answer:
      'Every listing states its status plainly. “Verified” is a confirmed vacancy under an active mandate. “Confidential Mandate” is also a confirmed vacancy — only the client’s name is withheld, and it is disclosed to you before your profile is ever presented. “Proposed Opportunity” is a role under discussion that is not yet confirmed. “Market-Matched” is not a vacancy at all; it lets you register interest for future mandates.',
    links: [{ label: 'Browse opportunities', to: '/opportunities' }],
  },
  {
    id: 'apply',
    keywords: ['apply', 'application form', 'how do i apply', 'submit an application'],
    answer:
      'Open any opportunity and use Apply. You will answer the screening questions set for that mandate and confirm that the compensation and location basis works for you. You can attach a résumé, but you are not required to have one on file to start.',
    links: [{ label: 'Browse opportunities', to: '/opportunities' }],
  },
  {
    id: 'career-services',
    keywords: ['career service', 'paid service', 'fee', 'cost', 'price', 'refund', 'do i have to pay'],
    answer:
      'Executive Career Services are optional and paid for by you directly. They are commercially separate from our search practice. Purchasing a service does not guarantee consideration, a match, an interview or a placement, and declining one has no effect on your candidacy. Each service publishes its scope, delivery terms, revision policy and refund policy before you commit.',
    links: [
      { label: 'Executive Career Services', to: '/career-services' },
      { label: 'Refund policy', to: '/legal/refund-policy' },
    ],
  },
  {
    id: 'employer',
    keywords: ['hire someone', 'hiring', 'employer', 'search mandate', 'recruit for us', 'we need a leader'],
    answer:
      'Start with a search inquiry — it begins a conversation, not a contract. We then complete organization verification, agree the specification in writing before research begins, and report progress at an agreed cadence. You only ever see candidates who have authorized presentation to your organization.',
    links: [
      { label: 'Discuss a search mandate', to: '/for-employers' },
      { label: 'Hiring solutions', to: '/employers/hiring-solutions' },
    ],
  },
  {
    id: 'services',
    keywords: ['your services', 'what do you do', 'retained search', 'board search', 'succession', 'assessment', 'benchmarking'],
    answer:
      'We are engaged in twelve ways, spanning search (retained, C-suite, board, confidential replacement, interim, portfolio and inclusive search), advisory (succession planning and leadership assessment) and intelligence (market mapping, talent intelligence and benchmarking). Each page sets out the methodology, deliverables and confidentiality safeguards.',
    links: [{ label: 'Executive Search services', to: '/services' }],
  },
  {
    id: 'industries',
    keywords: ['industry', 'industries', 'sector', 'practice area'],
    answer:
      'We maintain fourteen industry practices, each with its own view of the leadership market — the roles that recur, what the market rewards, and where requirements tend to be unrealistic.',
    links: [{ label: 'Industry practices', to: '/industries' }],
  },
  {
    id: 'match',
    keywords: ['match score', 'matching', 'algorithm', 'ai decision', 'am i qualified'],
    answer:
      'A match indication compares your profile against a mandate across ten dimensions and shows its reasoning for each. It is a ranking signal only. It is never used to reject anyone, and it is not a determination that you are qualified — a consultant reviews every match.',
  },
  {
    id: 'accessibility',
    keywords: ['accessib', 'adjustment', 'accommodation', 'screen reader', 'disability', 'dyslex'],
    answer:
      'Reasonable adjustments are available at any stage, and requesting one has no bearing on how you are assessed. Tell your consultant, or use the contact form, and we will arrange it.',
    links: [
      { label: 'Accessibility statement', to: '/legal/accessibility-statement' },
      { label: 'Contact us', to: '/contact' },
    ],
  },
  {
    id: 'fraud',
    keywords: ['scam', 'fraud', 'phishing', 'suspicious email', 'asked me for money'],
    answer:
      'We never request payment, banking details, identity documents by email, or passwords for any account other than your Velnoris portal. Any communication doing so is not from us. Please report it to us through the contact form.',
    links: [{ label: 'Report it', to: '/contact' }],
  },
  {
    id: 'contact',
    keywords: ['contact', 'speak to someone', 'talk to a human', 'consultant', 'phone', 'email you', 'customer support'],
    answer:
      'You can reach us at hello@velnorisexecutivenetwork.com, or use the contact form to route your message to the right queue — candidate support, employer search inquiry, privacy request or technical support. Each submission gets a reference number.',
    links: [{ label: 'Contact the firm', to: '/contact' }],
  },
];

/* ------------------------- Career and professional -------------------------- */

const careerTopics: AssistantTopic[] = [
  {
    id: 'interview-prep',
    keywords: ['interview', 'interviews', 'prepare for an interview', 'interview question', 'panel'],
    answer:
      'Work from evidence, not adjectives. Choose six situations from the last five years that show scope, judgement under pressure, a decision you got wrong, and something you built that outlasted you. For each, be able to state the situation in two sentences, what you personally decided, what it cost, and what the measurable outcome was. Rehearse the numbers — revenue, headcount, budget, timeline — because senior interviewers test whether you owned the thing or stood near it. Then prepare three questions of your own that only someone who has done the job would ask.',
  },
  {
    id: 'resume',
    keywords: ['resume', 'résumé', 'cv', 'write my cv', 'improve my resume'],
    answer:
      'At executive level a résumé is a scope document, not a duty list. Lead each role with the shape of what you ran — revenue, headcount, budget, geography, reporting line — then three to five outcomes with figures attached. Cut anything that reads as responsibility rather than result. Two pages is right for most; three is acceptable past twenty years. The summary at the top should say what you are for, not what you are like.',
    links: [{ label: 'Executive Résumé Writing', to: '/career-services/executive-resume-writing' }],
  },
  {
    id: 'negotiation',
    keywords: ['negotiat', 'salary', 'offer', 'compensation', 'counter offer', 'ask for more', 'pay rise', 'raise'],
    answer:
      'Negotiate the package, not the number. Establish the band before you name a figure, and let the employer anchor first if you can do it gracefully. When you do respond, give a range whose floor you would genuinely accept. Beyond base: bonus mechanics and who sets the targets, equity and its vesting, severance terms, notice, start date, and any guarantee covering what you forfeit by leaving. Ask for it all at once rather than in waves — sequential asks read as bad faith. And be able to say plainly why you are worth it, in outcomes.',
  },
  {
    id: 'career-direction',
    keywords: ['what should i do next', 'career change', 'not sure what i want', 'stuck', 'direction', 'next move', 'lost'],
    answer:
      'Start from evidence rather than aspiration. Look back over the last three years and mark the weeks you would happily repeat — then ask what they had in common: the type of problem, the scale, the people, the degree of autonomy. Most people find the pattern is about the work itself, not the title. Then test the direction cheaply before committing: two or three honest conversations with people already doing it will tell you more than another month of deliberation. If the answer is still unclear, the useful question is usually not “what do I want” but “what am I no longer willing to accept”.',
  },
  {
    id: 'first-90',
    keywords: ['first 90', 'first ninety', 'new role', 'just started', 'onboarding', '100 days'],
    answer:
      'Spend the first month listening in a structured way — the same handful of questions to twenty people, so patterns emerge rather than anecdotes. Find one visible, uncontroversial improvement to deliver by week six; it buys you permission for the harder work. Do not reorganize before you understand why the current shape exists. And decide early which two things you will be known for by the end of the year, because everything else will try to fill that space.',
  },
  {
    id: 'leadership',
    keywords: ['leadership', 'manage a team', 'my team', 'difficult conversation', 'underperform', 'manage up', 'delegate'],
    answer:
      'Most leadership difficulty is a clarity problem wearing a motivation costume. Before concluding someone will not do the work, check they know precisely what good looks like, that they have the authority to achieve it, and that nothing else you asked for is quietly competing with it. When the conversation is genuinely difficult, open with the specific observed behaviour and its consequence, not with a characterisation of the person — and say the hard sentence in the first minute. Delaying it is a kindness to yourself, not to them.',
  },
  {
    id: 'burnout',
    keywords: ['burnout', 'burnt out', 'exhausted', 'overwhelmed', 'stress', 'work life', 'balance', 'tired'],
    answer:
      'Burnout is rarely cured by a holiday, because it is usually structural rather than a deficit of rest. The useful audit is: which commitments are genuinely mine, which did I absorb because nobody else would, and which exist only because I have not said the sentence that ends them? Pick the single heaviest one and remove it properly rather than trimming five things by ten per cent. And if it has reached the point of affecting sleep or health, that is a matter for a doctor, not a productivity system — please treat it as one.',
  },
  {
    id: 'redundancy',
    keywords: ['redundan', 'laid off', 'lost my job', 'fired', 'let go', 'restructure'],
    answer:
      'First, this is a market event far more often than a verdict on you, and senior searches are usually decided on fit with a specific situation. Practically: get the terms in writing and have someone read them before you sign; agree the departure narrative with your employer so both of you tell the same true story; and tell your network early rather than waiting until you feel composed — people help far more readily when the news is fresh. Then treat the search as work with hours and structure, because unstructured job seeking is corrosive.',
  },
  {
    id: 'networking',
    keywords: ['network', 'networking', 'reach out', 'cold email', 'linkedin message', 'connections'],
    answer:
      'Ask for a specific thing, from a specific person, with a reason it is them. “Could I have twenty minutes on how your team structures regional P&L ownership?” is answerable; “can I pick your brain” is not. Keep the first message under six lines, state what you already know about their work so it cannot be a template, and propose two concrete times. Afterwards, send one line saying what you did with their advice — that single message is what turns a contact into a relationship.',
  },
  {
    id: 'board-seat',
    keywords: ['board seat', 'non executive', 'ned', 'first board', 'director role'],
    answer:
      'Boards recruit against a composition gap, not a general standard of excellence — so the question is not whether you are impressive but which specific gap you close. Name it: sector, function, transaction experience, regulatory exposure. A board résumé is a different document from an executive one; it foregrounds governance contribution, committee capability and independence, and it is short. Expect the first appointment to take longer than you think and to come through someone who has seen you operate.',
    links: [{ label: 'Board Résumé Preparation', to: '/career-services/board-resume-preparation' }],
  },
  {
    id: 'references-notice',
    keywords: ['reference', 'notice period', 'resign', 'hand in', 'garden leave', 'non compete', 'covenant'],
    answer:
      'Resign in person, briefly, and without relitigating the reasons — the conversation is an announcement, not a negotiation. Have the date agreed before you tell anyone else. On restrictive covenants: read them before you accept anything, because enforceability varies and the time to discover a problem is not after you have signed. References should be warned, briefed on the role, and thanked afterwards.',
  },
  {
    id: 'remote-relocation',
    keywords: ['remote', 'hybrid', 'relocat', 'move abroad', 'work from home', 'visa'],
    answer:
      'At senior level, the honest constraint is usually proximity to the thing you are accountable for. Site-based operations, regulated relationships and turnaround work rarely survive genuine remoteness, whatever the posting says. If you need flexibility, negotiate it as a specific pattern with an agreed review point rather than as a principle — concrete arrangements survive changes of manager, principles do not.',
  },
  {
    id: 'imposter',
    keywords: ['imposter', 'confidence', 'not good enough', 'doubt myself', 'nervous', 'anxious about'],
    answer:
      'The feeling is close to universal at the point where the work stops resembling what you were trained for — which is to say, it tracks growth rather than inadequacy. Two things help more than reassurance: write down the specific evidence for the decision you are doubting, and separate “I do not yet know this” from “I cannot learn this”, because they feel identical from the inside and are not remotely the same problem.',
  },
  {
    id: 'writing-help',
    keywords: ['write', 'draft', 'email to', 'letter', 'wording', 'how do i say', 'rephrase'],
    answer:
      'Tell me what the message needs to achieve, who is reading it, and any constraint you are working around, and I will draft it. A useful default for difficult professional writing: state the ask in the first sentence, give the reasoning in the second and third, and make the last line the easiest possible action for the reader. Warmth belongs in the wording, not in a delayed point.',
  },
];

/* ------------------------- Everyday and open topics ------------------------- */

const openTopics: AssistantTopic[] = [
  {
    id: 'greeting',
    keywords: ['hello', 'hi ', 'hey', 'good morning', 'good afternoon', 'good evening', 'how are you'],
    answer:
      'Hello. I am well, thank you — and ready to be useful. Ask me anything: something about Velnoris, something about your career, or something else entirely. Where shall we start?',
  },
  {
    id: 'identity',
    keywords: ['who are you', 'what are you', 'are you human', 'are you a bot', 'your name'],
    answer:
      'I am Velnoris AI, the assistant on this site. I answer questions about Velnoris from our published information, and I am happy to help with wider things too — career decisions, interviews, writing, or simply thinking something through. I am not a person, I cannot see your account or applications, and I never make decisions about anyone’s candidacy. When something needs a human, I will say so and point you to one.',
  },
  {
    id: 'capability',
    keywords: ['what can you do', 'can you help', 'help me', 'support', 'how can you help'],
    answer:
      'Quite a lot. On the platform: registering, verification statuses, privacy and consent, application statuses, services, and reaching a consultant. Beyond it: interview preparation, résumés, offer negotiation, leadership problems, career direction, drafting a difficult message, or just working through a decision out loud. Tell me the situation and I will give you something specific rather than generic.',
  },
  {
    id: 'thanks',
    keywords: ['thank', 'thanks', 'cheers', 'appreciate it', 'that helps'],
    answer: 'You are very welcome. If anything else comes up — professional or otherwise — I am here.',
  },
  {
    id: 'farewell',
    keywords: ['bye', 'goodbye', 'see you', 'that is all', 'nothing else'],
    answer: 'Goodbye, and good luck with it. Come back whenever you need to.',
  },
  {
    id: 'smalltalk',
    keywords: ['bored', 'how is your day', 'chat', 'talk to me', 'lonely', 'just talking'],
    answer:
      'Happy to talk. I am at my best when there is something concrete to chew on — a decision you are weighing, a conversation you are dreading, a piece of writing that will not come out right. But if you would rather just think aloud for a moment, go ahead; I will keep up.',
  },
  {
    id: 'time-live-data',
    keywords: ['weather', 'what time', 'today’s date', 'news', 'stock price', 'score', 'live'],
    answer:
      'I cannot reach live information — no weather, news, prices or clocks. I would rather tell you that than invent something. Anything that does not depend on today’s data, though, I can help with properly.',
  },
  {
    id: 'personal-decision',
    keywords: ['should i', 'what would you do', 'advice', 'decide', 'torn between', 'two options'],
    answer:
      'Give me the two options and what you are actually weighing, and I will think it through with you properly. A question I find cuts through most stalemates: in a year, which of these would you regret not having tried — and is the thing stopping you a real constraint or a preference you have not admitted to yet? Tell me the specifics and we can get more precise than that.',
  },
  {
    id: 'wellbeing-serious',
    keywords: ['depress', 'suicid', 'harm myself', 'cannot go on', 'hopeless', 'crisis'],
    answer:
      'I am sorry you are carrying that — it sounds genuinely heavy, and I do not want to hand you a platitude. I am not able to provide the kind of support this deserves. Please talk to someone who can: your doctor, or a crisis line in your country, which will have people available right now. If you are in immediate danger, please contact your local emergency services. If it would help to think through the work-related part of it afterwards, I am here for that.',
  },
  {
    id: 'legal-medical-financial',
    keywords: ['sue', 'lawyer', 'legal advice', 'tax', 'invest', 'diagnos', 'medical', 'symptom'],
    answer:
      'I can talk about this in general terms, but I am not a substitute for a lawyer, doctor or qualified financial adviser, and questions like this usually turn on specifics of your jurisdiction or circumstances. Tell me what you are trying to work out and I will help you frame it — including what to ask the professional you speak to, which is often where the real value is.',
  },
];

export const assistantTopics: AssistantTopic[] = [...platformTopics, ...careerTopics, ...openTopics];

/**
 * Used when nothing matches. Deliberately still engages with the question
 * rather than refusing, and offers a human route.
 */
export const ASSISTANT_FALLBACK =
  'I do not have a prepared answer for that one, and I would rather not invent detail. Tell me a little more — the situation, what you are trying to decide, or what a good outcome looks like — and I will give you something specific. If it concerns your account, an application, or anything only our team can see, a consultant is the right route.';

export const ASSISTANT_FALLBACK_LINKS: AssistantLink[] = [{ label: 'Contact the firm', to: '/contact' }];
